import { useEffect, useState, useRef } from 'react';
import {
    Box,
    TextField,
    Button,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableContainer,
    Paper,
    TableBody,
    TableCell,
    TableRow,
    Alert,
    AlertTitle
} from '@mui/material';
import {
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    MoreVert as MoreVertIcon,
    Add as AddIcon,
    Inventory2 as Inventory2Icon,
} from '@mui/icons-material';
import ContentLayout from '../ContentLayout/ContentLayout';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityOnIcon from '@mui/icons-material/Visibility';
import axios from '../../api/axios';
import { WithPermission } from '../../Auth/WithPermission';
import { useAuth } from '../../context/AuthContext';

const GET_BUILDERS = "/builder/";
const ADD_PLAN_OPTION = "/planoption/";
const ADD_ELEVATION = "/elevation/";
const ADD_PROJECT = "/project/";
const ADD_PLAN_NUMBER = "/plannumber/";

export const Builders = () => {
    const { userRole } = useAuth();

    const [addingItem, setAddingItem] = useState({
        type: null,
        builderId: null,
        childId: null,
        name: ""
    });
    const [buildersData, setBuildersData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuContext, setMenuContext] = useState(null);

    const [addingBuilder, setAddingBuilder] = useState(false);
    const [newBuilderName, setNewBuilderName] = useState('');
    const [builderNameError, setBuilderNameError] = useState('');

    const childAccordionRefs = useRef({});

    const canArchiveProject = (projectId) => {
        for (const builder of buildersData) {
            // Find the projects category in children
            const projectsCategory = builder.children?.find(child => child.name === 'Projects');
            if (projectsCategory && projectsCategory.items) {
                const project = projectsCategory.items.find(p => p.id === projectId);
                if (project) {
                    // Can only archive if all plans are archived (activePlans === 0) and there are plans
                    return project.plans >= 0 && project.activePlans === 0;
                }
            }
        }

        return false;
    };

    const canArchiveBuilder = (builderId) => {
        const builder = filteredBuilders.find(b => b.id === builderId);
        if (!builder) return false;

        let realPlansWithoutProjects = builder.plansWithoutProject - builder.archivedPlansWithoutProject;
        if (builder.projects == 0 && realPlansWithoutProjects == 0) {
            return true;
        }

        // Find the Projects category
        const projectsCategory = builder.children.find(child => child.name === 'Projects');
        if (!projectsCategory) {
            return false;
        }

        // Check if all projects are archived
        if (projectsCategory.active > 0) {
            return false;
        }

        // Check that all plans within projects are archived
        if (projectsCategory.items) {
            for (const project of projectsCategory.items) {
                // If any project has active plans, builder cannot be archived
                if (project.activePlans > 0) {
                    return false;
                }
            }
        }

        // Builder can only be archived if it has projects
        const hasProjects = projectsCategory.items && projectsCategory.items.length > 0;

        return hasProjects;
    };


    useEffect(() => {
        axios.post(GET_BUILDERS, {
            token: localStorage.getItem("token"),
            getListOfBuilders: "true"
        })
            .then((response) => {
                const buildersFromApi = response.data?.message || [];
                const transformedData = transformApiData(buildersFromApi);
                setBuildersData(transformedData);
            })
            .catch((error) => {
                console.error("Error fetching builders:", error);
            });
    }, []);

    const naturalCompare = (a, b) => {
        const ax = [], bx = [];

        (a + '').replace(/(\d+)|(\D+)/g, (_, $1, $2) => ax.push([$1 || Infinity, $2 || '']));
        (b + '').replace(/(\d+)|(\D+)/g, (_, $1, $2) => bx.push([$1 || Infinity, $2 || '']));

        while (ax.length && bx.length) {
            const an = ax.shift();
            const bn = bx.shift();
            const nn = (an[0] - bn[0]) || an[1].localeCompare(bn[1]);
            if (nn) return nn;
        }

        return ax.length - bx.length;
    };

    const transformApiData = (buildersFromApi) => {
        // Sort builders naturally by name
        const sortedBuilders = [...buildersFromApi].sort((a, b) =>
            naturalCompare(a.builderName || a.name, b.builderName || b.name)
        );

        return sortedBuilders.map(builder => {
            const projects = (builder.projects || []).sort((a, b) =>
                naturalCompare(a.projectName || a.name, b.projectName || b.name)
            );
            const planNumbers = (builder.planNumbers || []).sort((a, b) =>
                naturalCompare(a.planNumberName || a.name, b.planNumberName || b.name)
            );
            const elevations = (builder.elevations || []).sort((a, b) =>
                naturalCompare(a.elevationName || a.name, b.elevationName || b.name)
            );
            const planOptions = (builder.planOptions || []).sort((a, b) =>
                naturalCompare(a.planOptionName || a.name, b.planOptionName || b.name)
            );

            // Use the total plan count from backend (includes plans without projects)
            const totalBuilderPlans = builder.totalPlanCount || 0;

            // Calculate plans that belong to projects
            const projectPlansTotal = projects.reduce((sum, p) => sum + (p.planCount || 0), 0);

            return {
                id: builder.id,
                name: builder.builderName || builder.name,
                projects: projects.filter(p => !p.archived).length + projects.filter(p => p.archived).length,
                plans: totalBuilderPlans,
                archived: builder.archived,
                plansWithoutProject: builder.orphanedPlans,
                archivedPlansWithoutProject: builder.archivedOrphanedPlans,

                children: [
                    {
                        id: `projects-${builder.id}`,
                        name: "Projects",
                        active: projects.filter(p => !p.archived).length,
                        archived: projects.filter(p => p.archived).length,
                        totalProjects: projects.length,
                        totalPlans: projectPlansTotal, // Plans that belong to projects
                        items: projects.map(p => ({
                            id: p.id,
                            name: p.projectName || p.name,
                            archived: p.archived || false,
                            plans: p.planCount || 0,
                            activePlans: p.activePlans || 0,
                            archivedPlans: p.archivedPlans || 0
                        }))
                    },
                    {
                        id: `planNumbers-${builder.id}`,
                        name: "Plan numbers",
                        active: planNumbers.filter(pn => !pn.archived).length,
                        archived: planNumbers.filter(pn => pn.archived).length,
                        items: planNumbers.map(pn => ({
                            id: pn.id,
                            name: pn.planNumberName || pn.name,
                            archived: pn.archived || false
                        }))
                    },
                    {
                        id: `elevations-${builder.id}`,
                        name: "Elevations",
                        active: elevations.filter(e => !e.archived).length,
                        archived: elevations.filter(e => e.archived).length,
                        elevations: elevations.map(e => ({
                            id: e.id,
                            name: e.elevationName || e.name,
                            archived: e.archived || false
                        }))
                    },
                    {
                        id: `planOptions-${builder.id}`,
                        name: "Plan options",
                        active: planOptions.filter(po => !po.archived).length,
                        archived: planOptions.filter(po => po.archived).length,
                        items: planOptions.map(po => ({
                            id: po.id,
                            name: po.planOptionName || po.name,
                            archived: po.archived || false
                        }))
                    }
                ]
            };
        });
    };

    // Add these functions to handle the add functionality
    const [addingError, setAddingError] = useState("");

    const startAdding = (type, builderId, childId) => {
        setAddingError("")
        setAddingItem({
            type,
            builderId,
            childId,
            name: ""
        });
    };

    const cancelAdding = () => {
        setAddingItem({
            type: null,
            builderId: null,
            childId: null,
            name: ""
        });
    };

    const handleNameChange = (e) => {
        setAddingItem(prev => ({
            ...prev,
            name: e.target.value
        }));
    };

    const saveNewItem = async () => {
        if (!addingItem.name.trim()) {
            cancelAdding();
            return;
        }

        console.log("ADDING: ", addingItem)

        try {
            // Prepare API route based on type
            let apiUrl;
            const data = {
                token: localStorage.getItem("token"),
                builderId: addingItem.builderId,
            };

            if (addingItem.type === "plan option") {
                apiUrl = ADD_PLAN_OPTION;
                data.createPlanOption = true;
                data.planOptionName = addingItem.name;
            } else if (addingItem.type === "elevation") {
                apiUrl = ADD_ELEVATION;
                data.addElevation = true;
                data.elevationName = addingItem.name;
            } else if (addingItem.type === "plan number") {
                apiUrl = ADD_PLAN_NUMBER;
                data.createPlanNumber = true;
                data.planNumberName = addingItem.name;
            } else if (addingItem.type === "project") {
                apiUrl = ADD_PROJECT;
                data.addProject = true;
                data.projectName = addingItem.name;
            } else {
                console.warn("Unknown type:", addingItem.type);
                return;
            }

            const response = await axios.post(apiUrl, data);

            const raw =
                response.data?.message?.planoption ||
                response.data?.message?.elevation ||
                response.data?.message?.plannumber ||
                response.data?.message?.project;

            console.log(response)

            if (!raw) {
                setAddingError(response.data.message);
                console.error("problem with the adding of new one");
                return;
            }

            // **normalize** into the shape your table expects
            const newItem = {
                id: raw.id,
                name:
                    raw.projectName ??
                    raw.planNumberName ??
                    raw.elevationName ??
                    raw.planOptionName ??
                    "",
                archived: raw.archived ?? false,
                plans: raw.plansCount ?? 0,
                activePlans: 0
            };

            console.log("ADDED: ", newItem)

            // Add item to state
            setBuildersData((prev) =>
                prev.map((builder) => {
                    if (builder.id !== addingItem.builderId) return builder;

                    const updatedChildren = builder.children.map(child => {
                        if (child.id !== addingItem.childId) return child;
                        const itemsKey = child.items ? "items" : "elevations";

                        const updatedChild = {
                            ...child,
                            [itemsKey]: [newItem, ...child[itemsKey]],
                            active: child.active + 1,
                        };

                        // If this is the Projects child, also update totalProjects
                        if (addingItem.type === "project") {
                            updatedChild.totalProjects = (child.totalProjects || 0) + 1;
                        }

                        return updatedChild;
                    });


                    const newBuilder = {
                        ...builder,
                        children: updatedChildren,
                    };

                    if (addingItem.type === "project") {
                        newBuilder.projects = builder.projects + 1;
                    }

                    return newBuilder;
                })
            );
        } catch (error) {
            console.error("Failed to save item:", error);
        }

        cancelAdding();
    };



    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            saveNewItem();
        } else if (e.key === 'Escape') {
            cancelAdding();
        }
    };

    // Render function for accordion details
    const [hideArchivedProjects, setHideArchivedProjects] = useState(false);
    const [hideArchivedPlanNumbers, setHideArchivedPlanNumbers] = useState(false);
    const [hideArchivedElevations, setHideArchivedElevations] = useState(false);
    const [hideArchivedPlanOptions, setHideArchivedPlanOptions] = useState(false);

    const getCharacterLimit = (childId) => {
        if (childId.includes('projects-')) {
            return 50; // project limit
        } else if (childId.includes('planNumbers-')) {
            return 15; // plan number limit  
        } else if (childId.includes('elevations-')) {
            return 5; // elevation limit
        } else if (childId.includes('planOptions-')) {
            return 40; // plan option limit
        }

        return 50; // default limit for other types
    };

    const renderTableWithControls = (items, type, hideArchived, setHideArchived, builderId, childId) => {
        const isAdding = addingItem.type === type &&
            addingItem.builderId === builderId &&
            addingItem.childId === childId;

        const builder = buildersData.find(b => b.id === builderId);
        const canAddNewItem = builder.archived;

        return (
            <AccordionDetails>
                <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                    {(type !== "project" || ['superadmin', 'admin'].includes(userRole)) && !canAddNewItem && (
                        <Button
                            variant="text"
                            startIcon={<AddIcon />}
                            onClick={() => startAdding(type, builderId, childId)}
                            disabled={isAdding}
                        >
                            ADD {type.toUpperCase()}
                        </Button>
                    )}
                    {items.some(item => item.archived) && (
                        <Button
                            variant="text"
                            onClick={() => setHideArchived(!hideArchived)}
                            startIcon={!hideArchived ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
                        >
                            {!hideArchived ? 'HIDE ARCHIVED' : 'SHOW ARCHIVED'}
                        </Button>
                    )}
                </Box>
                <TableContainer
                    component={Paper}
                    sx={{
                        boxShadow: 'none',
                        border: 'none'
                    }}
                >
                    <Table size="small">
                        <TableBody>
                            {/* Add input row when in adding mode */}
                            {isAdding && (
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            variant="standard"
                                            value={addingItem.name}
                                            error={addingError !== ""}
                                            helperText={addingError}
                                            slotProps={{
                                                htmlInput: {
                                                    maxLength: getCharacterLimit(childId)
                                                }
                                            }}
                                            onChange={handleNameChange}
                                            onKeyDown={handleKeyPress}
                                            onBlur={saveNewItem}
                                            placeholder={`Enter ${type} name`}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* Existing items */}
                            {items
                                .filter(item => !hideArchived || !item.archived)
                                .map(item => (
                                    <TableRow key={item.id} hover>
                                        <TableCell
                                            sx={{
                                                color: item.archived ? 'text.disabled' : 'inherit',
                                                '& .MuiTypography-root': {
                                                    color: item.archived ? 'text.disabled' : 'inherit'
                                                }
                                            }}
                                        >
                                            {renamingItem?.type === 'child' &&
                                                renamingItem.id === item.id &&
                                                renamingItem.builderId === builderId &&
                                                renamingItem.childId === childId ? (
                                                <TextField
                                                    fullWidth
                                                    variant="standard"
                                                    value={renamingItem.newName}
                                                    onChange={handleRenameChange}
                                                    onKeyDown={handleRenameKeyPress}
                                                    error={renamingError !== ""}
                                                    slotProps={{
                                                        htmlInput: {
                                                            maxLength: getCharacterLimit(childId)
                                                        }
                                                    }}
                                                    helperText={renamingError}
                                                    inputRef={renameInputRef}

                                                    // onBlur={() => isRenaming && saveRenaming()}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {item.archived && <Inventory2Icon fontSize="small" />}
                                                    <Typography component="span">
                                                        {item.name}
                                                    </Typography>

                                                    {childId?.includes('projects-') && (
                                                        <Typography component="span" color="text.secondary" variant="body2">
                                                            (Plans: {item.activePlans} active, {item.archivedPlans} archived)
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {/* Show menu icon only if there are available actions */}
                                            {(item.archived ||
                                                (!childId?.includes('projects-') ||
                                                    ['superadmin', 'admin'].includes(userRole))) && !builder.archived && (
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMenuOpen(e, {
                                                                type: 'child',
                                                                id: item.id,
                                                                childId: childId,
                                                                builderId: builderId,
                                                                archived: item.archived
                                                            });
                                                        }}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </AccordionDetails>
        );
    };

    // Update your renderChildDetails function
    const renderChildDetails = (child, builderId) => {
        // Handle different child types
        switch (child.name.toLowerCase()) {
            case 'projects':
                return renderTableWithControls(
                    child.items,
                    'project',
                    hideArchivedProjects,
                    setHideArchivedProjects,
                    builderId,
                    child.id
                );

            case 'plan numbers':
                return renderTableWithControls(
                    child.items,
                    'plan number',
                    hideArchivedPlanNumbers,
                    setHideArchivedPlanNumbers,
                    builderId,
                    child.id
                );

            case 'elevations':
                return renderTableWithControls(
                    child.elevations,
                    'elevation',
                    hideArchivedElevations,
                    setHideArchivedElevations,
                    builderId,
                    child.id
                );

            case 'plan options':
                return renderTableWithControls(
                    child.items,
                    'plan option',
                    hideArchivedPlanOptions,
                    setHideArchivedPlanOptions,
                    builderId,
                    child.id
                );

            default:
                return null;
        }
    };

    const handleAddBuilder = () => {
        setAddingBuilder(true);
        setNewBuilderName('');
        setBuilderNameError('');
    };

    const validateBuilderName = (name) => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            return "Builder name cannot be empty";
        }

        // const existingBuilder = buildersData.find(
        //     builder => builder.name.toLowerCase() === trimmedName.toLowerCase()
        // );

        // if (existingBuilder) {
        //     return "A builder with this name already exists";
        // }

        return '';
    };

    const [hideArchivedBuilders, sethideArchivedBuilders] = useState(false)

    const handleBuilderNameChange = (e) => {
        const value = e.target.value;
        setNewBuilderName(value);

        // Real-time validation
        const error = validateBuilderName(value);
        setBuilderNameError(error);
    };

    const handleBuilderKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveNewBuilder();
        } else if (e.key === 'Escape') {
            cancelAddingBuilder();
        }
    };

    const cancelAddingBuilder = () => {
        setAddingBuilder(false);
        setNewBuilderName('');
        setBuilderNameError('');
    };

    const saveNewBuilder = async () => {
        const trimmedName = newBuilderName.trim();

        if (!trimmedName) {
            cancelAddingBuilder();
            return;
        }

        if (trimmedName.length > 50) {
            setBuilderNameError("Builder name cannot exceed 50 characters.");
            return;
        }

        const validationError = validateBuilderName(trimmedName);
        if (validationError) {
            setBuilderNameError(validationError);
            return;
        }

        try {
            const response = await axios.post(GET_BUILDERS, {
                token: localStorage.getItem("token"),
                createBuilder: true,
                builderName: trimmedName
            });

            const newBuilder = response.data?.message?.builder;

            if (!newBuilder) {
                setBuilderNameError(response.data.message);
                console.error("No builder returned from API");
                return;
            }

            // Transform new builder to match existing data structure
            const transformedBuilder = {
                id: newBuilder.id,
                name: newBuilder.builderName || newBuilder.name,
                projects: 0,
                plans: 0,
                children: [
                    {
                        id: `projects-${newBuilder.id}`,
                        name: "Projects",
                        active: 0,
                        archived: 0,
                        items: []
                    },
                    {
                        id: `planNumbers-${newBuilder.id}`,
                        name: "Plan numbers",
                        active: 0,
                        archived: 0,
                        items: []
                    },
                    {
                        id: `elevations-${newBuilder.id}`,
                        name: "Elevations",
                        active: 0,
                        archived: 0,
                        elevations: []
                    },
                    {
                        id: `planOptions-${newBuilder.id}`,
                        name: "Plan options",
                        active: 0,
                        archived: 0,
                        items: []
                    }
                ]
            };

            // Add to state with natural sorting
            setBuildersData(prev => {
                const updatedBuilders = [transformedBuilder, ...prev];
                return updatedBuilders.sort((a, b) => naturalCompare(a.name, b.name));
            });

            console.log("Successfully added builder:", transformedBuilder);

        } catch (error) {
            console.error("Failed to create builder:", error);
            setBuilderNameError("Failed to create builder. Please try again.");
            return;
        }

        cancelAddingBuilder();
    };

    const handleMenuOpen = (e, context) => {
        setMenuAnchorEl(e.currentTarget);
        console.log(buildersData)
        setMenuContext(context);
        console.log(context)
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuContext(null);
    };

    // Add this state near your other state declarations
    const renameInputRef = useRef(null);
    const builderRenameInputRef = useRef(null);
    const [renamingItem, setRenamingItem] = useState(null);
    const [isRenaming, setIsRenaming] = useState(false);
    const [renamingError, setRenamingError] = useState("");

    // Add these functions to your component
    const startRenaming = (context) => {
        setIsRenaming(true);

        // Your existing logic to set renamingItem
        const builder = buildersData.find(b => b.id === context.builderId);
        let currentName = '';
        let itemType = '';

        if (builder) {
            const child = builder.children.find(c => c.id === context.childId);
            if (child) {
                itemType = child.name.toLowerCase();
                const itemsKey = child.items ? 'items' : 'elevations';
                const item = child[itemsKey]?.find(i => i.id === context.id);
                currentName = item?.name || '';
            }
        }

        setRenamingItem({
            type: 'child',
            itemType: itemType,
            id: context.id,
            builderId: context.builderId,
            childId: context.childId,
            name: currentName,
            newName: currentName
        });

        // Focus after state update
        setTimeout(() => {
            if (renameInputRef.current) {
                renameInputRef.current.focus();
            }
        }, 100);
    };

    const cancelRenaming = () => {
        setRenamingItem(null);
        setIsRenaming(false);
    };


    const handleRenameChange = (e) => {
        setRenamingItem(prev => ({
            ...prev,
            newName: e.target.value
        }));
    };

    const saveRenaming = async () => {
        if (!isRenaming || !renamingItem?.newName?.trim()) {
            cancelRenaming();
            return;
        }

        const commonPayload = {
            token: localStorage.getItem('token'),
            id: renamingItem.id,
            builderId: renamingItem.builderId
        };

        try {
            let apiUrl;
            let payload = { ...commonPayload };

            // Normalize the type for comparison
            console.log(renamingItem)
            const itemType = renamingItem.itemType.toLowerCase();

            if (itemType.includes('builder')) {
                apiUrl = GET_BUILDERS;

                let trimmedName = renamingItem.newName?.trim() || "";

                if (trimmedName.length > 50) {
                    setBuilderNameError("Builder name cannot exceed 50 characters.");
                    return;
                }

                payload.renameBuilder = true;
                payload.newBuilderName = trimmedName;
            }
            else if (itemType.includes('project')) {
                apiUrl = ADD_PROJECT;
                payload.renameProject = true;
                payload.newProjectName = renamingItem.newName;
            }
            else if (itemType.includes('plan number')) {
                apiUrl = ADD_PLAN_NUMBER;
                payload.renamePlanNumber = true;
                payload.newPlanNumberName = renamingItem.newName;
            }
            else if (itemType.includes('elevation')) {
                apiUrl = ADD_ELEVATION;
                payload.renameElevation = true;
                payload.newElevationName = renamingItem.newName;
            }
            else if (itemType.includes('plan option')) {
                apiUrl = ADD_PLAN_OPTION;
                payload.renamePlanOption = true;
                payload.newPlanOptionName = renamingItem.newName;
            }
            else {
                console.warn('Unknown rename type:', renamingItem.type);
                cancelRenaming();
                return;
            }

            const response = await axios.post(apiUrl, payload);

            console.log(response)
            if (response.data?.code === 0) {
                console.log(`Renamed ${renamingItem.type} to: ${renamingItem.newName}`);

                // Update local state
                setBuildersData(prev =>
                    prev.map(builder => {
                        if (itemType.includes('builder') && builder.id === renamingItem.id) {
                            return { ...builder, name: renamingItem.newName };
                        }

                        if (builder.id === renamingItem.builderId) {
                            const updatedChildren = builder.children.map(child => {
                                const itemsKey = child.items ? 'items' : 'elevations';

                                if (child.id === renamingItem.childId) {
                                    return {
                                        ...child,
                                        [itemsKey]: child[itemsKey].map(item =>
                                            item.id === renamingItem.id
                                                ? { ...item, name: renamingItem.newName }
                                                : item
                                        )
                                    };
                                }
                                return child;
                            });
                            return { ...builder, children: updatedChildren };
                        }
                        return builder;
                    })
                );

                setRenamingItem(null);
                setIsRenaming(false);
            } else {
                console.error('Rename failed:', response.data);
                setRenamingError(response.data.message)
            }
        } catch (error) {
            console.error(`Error renaming ${renamingItem.type}:`, error);
        }
    };

    const handleRenameKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveRenaming();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelRenaming();
        } else if (e.key === ' ') {
            e.stopPropagation();
        }
    };

    const handleRenameBuilder = () => {
        setIsRenaming(true);
        setRenamingError("")
        const builder = buildersData.find(b => b.id === menuContext.id);
        setRenamingItem({
            type: 'builder',
            itemType: "builder",
            id: menuContext.id,
            name: builder?.name || '',
            newName: builder?.name || ''
        });

        handleMenuClose();

        setTimeout(() => {
            if (builderRenameInputRef.current) {
                builderRenameInputRef.current.focus();
            }
        }, 100);
    };

    const handleArchiveBuilder = () => {
        const builder = buildersData.find(b => b.id === menuContext.id);
        handleMenuClose();
        axios.post(GET_BUILDERS, {
            token: localStorage.getItem('token'),
            archiveBuilder: true,
            archive: !builder.archived,
            id: builder.id
        })
            .then((response) => {
                console.log(response)
                setBuildersData(prevData =>
                    prevData.map(b =>
                        b.id === builder.id
                            ? { ...b, archived: !builder.archived }
                            : b
                    )
                );
            })
            .catch((error) => {
                console.error('Error updating builder archive status:', error);
            });
    };

    const handleRename = () => {
        console.log("RENAMING START")
        setRenamingError("")
        startRenaming(menuContext);
        handleMenuClose();
    };

    const handleArchive = async () => {
        console.log("Archiving", menuContext);

        const builder = buildersData.find(b => b.id === menuContext.builderId);
        let itemType = '';

        if (builder) {
            const child = builder.children.find(c => c.id === menuContext.childId);
            if (child) {
                itemType = child.name.toLowerCase();
            }
        }

        const commonPayload = {
            token: localStorage.getItem('token'),
            id: menuContext.id,
            builderId: menuContext.builderId,
            archive: true
        };

        let apiUrl;
        let payload = { ...commonPayload };

        if (itemType.includes('plan number')) {
            apiUrl = ADD_PLAN_NUMBER;
            payload.archivePlanNumber = true;
        }
        else if (itemType.includes('elevation')) {
            apiUrl = ADD_ELEVATION;
            payload.archiveElevation = true;
        }
        else if (itemType.includes('plan option')) {
            apiUrl = ADD_PLAN_OPTION;
            payload.archivePlanOption = true;
        }
        else if (itemType.includes('project')) {
            apiUrl = ADD_PROJECT;
            payload.archiveProject = true;
        }
        else {
            console.warn('Unknown archive type:', itemType);
            return;
        }

        try {
            handleMenuClose();
            const response = await axios.post(apiUrl, payload);

            if (response.data?.code === 0) {
                console.log("Successfully archived:", response.data);

                // Update local state
                setBuildersData(prev =>
                    prev.map(b => {
                        if (b.id !== menuContext.builderId) return b;

                        const updatedChildren = b.children.map(c => {
                            if (c.id !== menuContext.childId) return c;

                            const itemsKey = c.items ? 'items' : 'elevations';
                            const updatedItems = c[itemsKey].map(item => {
                                if (item.id === menuContext.id) {
                                    return { ...item, archived: true };
                                }
                                return item;
                            });

                            // Update active/archived counts
                            const activeCount = updatedItems.filter(i => !i.archived).length;
                            const archivedCount = updatedItems.length - activeCount;

                            return {
                                ...c,
                                [itemsKey]: updatedItems,
                                active: activeCount,
                                archived: archivedCount
                            };
                        });

                        return {
                            ...b,
                            children: updatedChildren
                        };
                    })
                );
            } else {
                console.error("Archive failed:", response.data);
            }
        } catch (error) {
            console.error("Error archiving item:", error);
        }
    };

    const handleRestore = async () => {
        console.log("Restoring", menuContext);

        const builder = buildersData.find(b => b.id === menuContext.builderId);
        let itemType = '';

        if (builder) {
            const child = builder.children.find(c => c.id === menuContext.childId);
            if (child) {
                itemType = child.name.toLowerCase();
            }
        }

        const commonPayload = {
            token: localStorage.getItem('token'),
            id: menuContext.id,
            builderId: menuContext.builderId,
            archive: false
        };

        let apiUrl;
        let payload = { ...commonPayload };

        if (itemType.includes('plan number')) {
            apiUrl = ADD_PLAN_NUMBER;
            payload.archivePlanNumber = true;
        }
        else if (itemType.includes('elevation')) {
            apiUrl = ADD_ELEVATION;
            payload.archiveElevation = true;
        }
        else if (itemType.includes('plan option')) {
            apiUrl = ADD_PLAN_OPTION;
            payload.archivePlanOption = true;
        }
        else if (itemType.includes('project')) {
            apiUrl = ADD_PROJECT;
            payload.archiveProject = true;
        }
        else {
            console.warn('Unknown archive type:', itemType);
            return;
        }

        try {
            handleMenuClose();
            const response = await axios.post(apiUrl, payload);

            if (response.data?.code === 0) {
                console.log("Successfully restored:", response.data);

                // Update local state
                setBuildersData(prev =>
                    prev.map(b => {
                        if (b.id !== menuContext.builderId) return b;

                        const updatedChildren = b.children.map(c => {
                            if (c.id !== menuContext.childId) return c;

                            const itemsKey = c.items ? 'items' : 'elevations';
                            const updatedItems = c[itemsKey].map(item => {
                                if (item.id === menuContext.id) {
                                    return { ...item, archived: false };
                                }
                                return item;
                            });

                            // Update active/archived counts
                            const activeCount = updatedItems.filter(i => !i.archived).length;
                            const archivedCount = updatedItems.length - activeCount;

                            return {
                                ...c,
                                [itemsKey]: updatedItems,
                                active: activeCount,
                                archived: archivedCount
                            };
                        });

                        return {
                            ...b,
                            children: updatedChildren
                        };
                    })
                );
            } else {
                console.error("Restoring failed:", response.data);
            }
        } catch (error) {
            console.error("Error restoring item:", error);
        }
    };

    const accordionRefs = useRef({});

    const [expandedItems, setExpandedItems] = useState({
        builder: null,
        child: null
    });

    const getHeaderHeight = () => {
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue('--app-header-height');
    return parseInt(value, 10) || 0;
    };

    const smartScroll = (element) => {
    if (!element) return;

    setTimeout(() => {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const headerHeight = getHeaderHeight();

        const topVisible = rect.top >= headerHeight;
        const bottomVisible = rect.bottom <= viewportHeight;

        if (topVisible && bottomVisible) return;

        const y = window.scrollY + rect.top - headerHeight;
        window.scrollTo({ top: y, behavior: 'smooth' });
    }, 200);
    };

    const handleExpandBuilder = (id) => (e, isExpanded) => {
    if (renamingItem?.type === 'builder' && renamingItem.id === id) return;

    setExpandedItems(prev => ({
        builder: isExpanded ? id : null,
        child: null
    }));

    if (isExpanded) {
        setTimeout(() => {
        smartScroll(accordionRefs.current[id]);
        }, 200);
    }
    };

    const handleExpandChild = (id) => (e, isExpanded) => {
    setExpandedItems(prev => ({
        ...prev,
        child: isExpanded ? id : null
    }));

    if (isExpanded) {
        setTimeout(() => {
        smartScroll(childAccordionRefs.current[id]);
        }, 200);
    }
    };

    const formatStats = (item) => {
        // For top-level builders
        if (item.projects !== undefined && item.plans !== undefined) {
            const projectsData = item.children.find(child => child.name === "Projects");
            let realPlansWithoutProjects = item.plansWithoutProject - item.archivedPlansWithoutProject;
            let activePlans = realPlansWithoutProjects || 0;
            let archivedPlans = item.archivedPlansWithoutProject || 0;

            if (projectsData && projectsData.items) {
                projectsData.items.forEach(project => {
                    activePlans += project.activePlans || 0;
                    archivedPlans += project.archivedPlans || 0;
                });
            }
            return `(Projects: ${projectsData?.active || 0} active, ${projectsData?.archived || 0} archived | Plans: ${activePlans} active, ${archivedPlans} archived)`;
        }

        // For Projects child specifically - show project stats and plan stats
        if (item.name === "Projects" && item.totalProjects !== undefined) {
            // Calculate active and archived plans from all projects
            const totalActivePlans = item.items ? item.items.reduce((sum, project) => sum + (project.activePlans || 0), 0) : 0;
            const totalArchivedPlans = item.items ? item.items.reduce((sum, project) => sum + (project.archivedPlans || 0), 0) : 0;

            return `(${item.active} active, ${item.archived} archived | Plans: ${totalActivePlans} active, ${totalArchivedPlans} archived)`;
        }

        // For other children (Plan numbers, Elevations, Plan options)
        if (item.active !== undefined && item.archived !== undefined) {
            return `(${item.active} active, ${item.archived} archived)`;
        }

        return '';
    };

    const filteredBuilders = buildersData.filter(b => b.name?.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <>
        {userRole === 'useredit' &&
            <Box sx={{ paddingLeft: 5, paddingRight: 5, paddingTop: 2 }}>
                <Alert severity="info">
                    <AlertTitle>Limited access for Editor role</AlertTitle>
                    You can configure plan numbers, elevations, and plan options. Only Admins can modify builders and projects.
                </Alert>
            </Box>
        }
            <ContentLayout title={"Builders"} url={"/plans"}>
                <Box sx={{ display: 'flex', justifyContent: "space-between", mb: 3 }}>
                    <Box>
                        <Button
                            variant="text"
                            onClick={() => sethideArchivedBuilders(!hideArchivedBuilders)}
                            startIcon={!hideArchivedBuilders ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
                        >
                            {!hideArchivedBuilders ? 'HIDE ARCHIVED BUILDERS' : 'SHOW ARCHIVED BUILDERS'}
                        </Button>
                        <WithPermission rolesRequired={['admin', 'superadmin']}>
                            <Button
                                variant="text"
                                startIcon={<AddIcon />}
                                onClick={handleAddBuilder}
                                disabled={addingBuilder}
                            >
                                ADD BUILDER
                            </Button>
                        </WithPermission>
                    </Box>

                    {/* <TextField
                        placeholder="Find a builder, project..."
                        variant="standard"
                        size="small"
                        sx={{ ml: 'auto' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                            <SearchIcon />
                            </InputAdornment>
                        ),
                        sx: { width: 250 }
                        }}
                    /> */}
                </Box>


                {/* Builders Accordion */}
                <Box>
                    {addingBuilder && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <TextField
                                autoFocus
                                fullWidth
                                variant="standard"
                                value={newBuilderName}
                                onChange={handleBuilderNameChange}
                                onKeyDown={handleBuilderKeyPress}
                                onBlur={saveNewBuilder}
                                slotProps={{
                                    htmlInput: {
                                        maxLength: getCharacterLimit("builder")
                                    }
                                }}
                                placeholder="Enter builder name"
                                error={!!builderNameError}
                                helperText={builderNameError}
                            />
                        </Box>
                    )}

                    {filteredBuilders.filter(builder => !hideArchivedBuilders || !builder.archived).map((builder) => (
                        <Accordion
                            key={builder.id}
                            ref={(el) => accordionRefs.current[builder.id] = el}
                            expanded={expandedItems.builder === builder.id}
                            onChange={handleExpandBuilder(builder.id)}
                            sx={{ mb: 2, color: builder.archived ? 'text.disabled' : 'inherit' }}
                            slotProps={{
                                transition: {
                                    timeout: 150,
                                },
                            }}
                        >
                            <AccordionSummary
                                sx={{
                                    flexDirection: 'row-reverse',
                                    gap: '8px',
                                    '& .MuiAccordionSummary-expandIconWrapper': {
                                        marginRight: 0,
                                        transform: 'rotate(-90deg)',
                                        transition: 'transform 0.2s ease',
                                    },
                                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                        transform: 'rotate(0deg)',
                                    }
                                }}
                                expandIcon={<ExpandMoreIcon />}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1 }}>
                                    {builder.archived && <Inventory2Icon fontSize="small" />}

                                    {renamingItem?.type === 'builder' && renamingItem.id === builder.id ? (
                                        <TextField
                                            fullWidth
                                            value={renamingItem.newName}
                                            onChange={handleRenameChange}
                                            onKeyDown={handleRenameKeyPress}
                                            error={renamingError !== ""}
                                            helperText={renamingError}
                                            inputRef={builderRenameInputRef}
                                            slotProps={{
                                                htmlInput: {
                                                    maxLength: getCharacterLimit("builder")
                                                }
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            variant="standard"
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: 'auto',
                                                    fontWeight: 600
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Typography fontWeight={expandedItems.builder === builder.id ? 600 : 400}>
                                            {builder.name}
                                        </Typography>
                                    )}
                                    <Typography sx={{ ml: 1 }}>
                                        {formatStats(builder)}
                                    </Typography>
                                </Box>
                                <WithPermission rolesRequired={['admin', 'superadmin']}>
                                    <Box
                                        component="div"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMenuOpen(e, { type: 'builder', id: builder.id, archived: builder.archived });
                                        }}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            backgroundColor: 'transparent',
                                            outline: 0,
                                            border: 0,
                                            margin: 0,
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            verticalAlign: 'middle',
                                            appearance: 'none',
                                            textDecoration: 'none',
                                            textAlign: 'center',
                                            flex: '0 0 auto',
                                            fontSize: '1.5rem',
                                            padding: '8px',
                                            borderRadius: '50%',
                                            overflow: 'visible',
                                            color: 'action.active',
                                            transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                                '@media (hover: none)': {
                                                    backgroundColor: 'transparent'
                                                }
                                            }
                                        }}
                                    >
                                        <MoreVertIcon />
                                    </Box>
                                </WithPermission>
                            </AccordionSummary>

                            <AccordionDetails>
                                {builder.children.map(child => (
                                    <Accordion
                                        key={child.id}
                                        ref={(el) => childAccordionRefs.current[child.id] = el}
                                        expanded={expandedItems.child === child.id}
                                        onChange={handleExpandChild(child.id)}
                                        sx={{
                                            mb: 2,
                                            '&:before': {
                                                display: 'none' // Removes the border
                                            },
                                            boxShadow: 'none', // Removes shadow if present
                                            color: builder.archived ? 'text.disabled' : 'inherit'
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                                flexDirection: 'row-reverse',
                                                gap: '8px',
                                                '& .MuiAccordionSummary-expandIconWrapper': {
                                                    marginRight: 0,
                                                    transform: 'rotate(-90deg)',
                                                    transition: 'transform 0.2s ease',
                                                },
                                                '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                                    transform: 'rotate(0deg)',
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                <Typography fontWeight={expandedItems.child === child.id ? 600 : 400}>
                                                    {child.name}
                                                </Typography>
                                                <Typography sx={{ ml: 1 }}>
                                                    {formatStats(child)}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        {renderChildDetails(child, builder.id)}
                                    </Accordion>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                {/* Shared Menu */}
                <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}
                >
                    {/* For Builders (top level) - always show only Rename */}
                    {menuContext?.type === 'builder' && (
                        menuContext?.archived ? (
                            <MenuItem key="restore" onClick={handleArchiveBuilder}>
                                Restore
                            </MenuItem>
                        ) : [
                            <MenuItem key="rename" onClick={handleRenameBuilder}>
                                Rename
                            </MenuItem>,
                            <MenuItem
                                key="archive"
                                onClick={handleArchiveBuilder}
                                disabled={!canArchiveBuilder(menuContext?.id)}
                                sx={{ color: (!canArchiveBuilder(menuContext?.id)) ? "gray" : "red" }}
                            >
                                Archive
                            </MenuItem>
                        ]
                    )}

                    {/* For actual items under categories */}
                    {(menuContext?.type === 'child' && menuContext?.hasOwnProperty('archived')) && (
                        menuContext?.archived ? (
                            <MenuItem onClick={handleRestore}>Restore</MenuItem>
                        ) : [
                            // Rename option - hide for projects if user is useredit
                            !(menuContext?.childId?.includes('projects-') && userRole === 'useredit') && (
                                <MenuItem key="rename" onClick={handleRename}>Rename</MenuItem>
                            ),

                            // Archive button with conditional enabling
                            <MenuItem
                                key="archive"
                                onClick={handleArchive}
                                disabled={
                                    // Disable if this is a project and it has active plans
                                    menuContext?.childId?.includes('projects-') &&
                                    !canArchiveProject(menuContext?.id)
                                }
                                sx={{
                                    color: (
                                        menuContext?.childId?.includes('projects-') &&
                                        !canArchiveProject(menuContext?.id)
                                    ) ? "gray" : "red"
                                }}
                            >
                                Archive
                            </MenuItem>

                        ].filter(Boolean) // Remove any false values from the array
                    )}
                </Menu>
            </ContentLayout>
        </>
    );
};